import type { Novidade } from './tipos';

/**
 * Fase 2 (W31): navegação e menus. Uma novidade por mudança visível: o menu lateral (espiada, idioma, modo discreto e
 * Notificações), a página de Índices, as abas de Cadastros com os serviços contratados, o Financeiro numa linha só,
 * Relatórios no menu lateral e a correção da Versão no ar.
 */
export const novidadeMenuLateral: Novidade = {
  id: '2026-10-05-menu-lateral',
  publicada_em: '2026-10-05',
  tipo: 'melhoria',
  modulo: null,
  permissao: null,
  rota: '/conta/notificacoes',
  textos: {
    pt: {
      titulo: 'Menu lateral reorganizado e Notificações no menu',
      resumo: 'O sino virou o item Notificações, com o número de não lidas. Idioma, tema e modo discreto ficam no rodapé do menu.',
      passos: [
        'Notificações fica logo abaixo de Início no menu lateral, com o número de avisos não lidos.',
        'No rodapé do menu, abaixo do seu nome, ficam o tema, o idioma, o olho e as preferências do modo discreto, e o Sair.',
        'O botão de recolher o menu fica no topo, ao lado da marca. Recolhido, o idioma vira um ícone que abre as opções.',
        'Com o menu recolhido, ele só abre por cima ao deixar o mouse parado sobre os ícones por 10 segundos; pelo teclado, abre na hora.',
      ],
    },
    en: {
      titulo: 'Side menu reorganized and Notifications in the menu',
      resumo: 'The bell became the Notifications item, with the unread count. Language, theme and discreet mode sit in the menu footer.',
      passos: [
        'Notifications sits right below Home in the side menu, with the number of unread notices.',
        'In the menu footer, below your name, you find the theme, the language, the eye and the discreet mode preferences, and Sign out.',
        'The collapse button sits at the top, next to the brand. Collapsed, the language becomes an icon that opens the options.',
        'With the menu collapsed, it only opens on top after the mouse rests on the icons for 10 seconds; with the keyboard, it opens at once.',
      ],
    },
    es: {
      titulo: 'Menú lateral reorganizado y Notificaciones en el menú',
      resumo: 'La campana pasó a ser el ítem Notificaciones, con el número de no leídas. Idioma, tema y modo discreto van en el pie del menú.',
      passos: [
        'Notificaciones queda justo debajo de Inicio en el menú lateral, con el número de avisos sin leer.',
        'En el pie del menú, debajo de tu nombre, están el tema, el idioma, el ojo y las preferencias del modo discreto, y Salir.',
        'El botón para contraer el menú queda arriba, junto a la marca. Contraído, el idioma pasa a ser un ícono que abre las opciones.',
        'Con el menú contraído, solo se abre encima al dejar el mouse quieto sobre los íconos por 10 segundos; con el teclado, se abre al instante.',
      ],
    },
  },
  imagens: ['/novidades/2026-10-05-menu-lateral/menu.webp', null, null, null],
  treinamento: { curso: 'novidades' },
};

export const novidadeIndices: Novidade = {
  id: '2026-10-05-indices',
  publicada_em: '2026-10-05',
  tipo: 'melhoria',
  modulo: 'financeiro',
  permissao: null,
  rota: '/financeiro/financeiro/indices',
  textos: {
    pt: {
      titulo: 'Índices em página própria',
      resumo: 'IGP-M, IPCA, INPC e SELIC saíram da barra superior e ganharam a página Financeiro > Índices, com o histórico mês a mês.',
      passos: [
        'Abra Financeiro > Índices: cada índice mostra a variação do último mês e o acumulado de 12 meses.',
        'No Histórico mensal, escolha o índice para ver os últimos 24 meses em tabela.',
        'Quem administra os conectores pede a atualização ao Banco Central em Atualizar índices agora.',
      ],
    },
    en: {
      titulo: 'Indices on their own page',
      resumo: 'IGP-M, IPCA, INPC and SELIC left the top bar and got the Finance > Indices page, with the month by month history.',
      passos: [
        'Open Finance > Indices: each index shows the change of the last month and the 12-month accumulated.',
        'In Monthly history, choose the index to see the last 24 months in a table.',
        'Whoever manages the connectors asks the Central Bank for an update in Update indices now.',
      ],
    },
    es: {
      titulo: 'Índices en página propia',
      resumo: 'IGP-M, IPCA, INPC y SELIC salieron de la barra superior y tienen la página Finanzas > Índices, con el historial mes a mes.',
      passos: [
        'Abre Finanzas > Índices: cada índice muestra la variación del último mes y el acumulado en 12 meses.',
        'En Historial mensual, elige el índice para ver los últimos 24 meses en tabla.',
        'Quien administra los conectores pide la actualización al Banco Central en Actualizar índices ahora.',
      ],
    },
  },
  imagens: ['/novidades/2026-10-05-indices/indices.webp', null, null],
  treinamento: { curso: 'novidades' },
};

export const novidadeCadastrosAbas: Novidade = {
  id: '2026-10-05-cadastros-abas',
  publicada_em: '2026-10-05',
  tipo: 'melhoria',
  modulo: 'cadastros',
  permissao: 'cadastros.ver',
  rota: '/cadastros/cadastros/servicos-contratados',
  textos: {
    pt: {
      titulo: 'Cadastros em cinco abas e os serviços contratados',
      resumo: 'Cadastros, Serviços, Financeiro, Agenda e Estoque - Produtos, cada uma com as suas sub-abas. E um cadastro novo de serviços contratados.',
      passos: [
        'Em Cadastros, as abas de cima são Cadastros, Serviços, Financeiro, Agenda e Estoque - Produtos; as sub-abas ficam logo abaixo.',
        'Contas e caixas virou Contas financeiras e Categorias virou Plano de contas, as duas na aba Financeiro.',
        'Em Serviços, o Catálogo de serviços é o que a empresa vende; Serviços é o cadastro novo do que ela contrata de terceiros.',
        'No serviço contratado, escolha a categoria de despesa e o fornecedor padrão: a compra direta sugere os dois no item.',
      ],
    },
    en: {
      titulo: 'Records in five tabs and contracted services',
      resumo: 'Records, Services, Finance, Schedule and Inventory - Products, each with its sub-tabs. And a new record of contracted services.',
      passos: [
        'In Records, the top tabs are Records, Services, Finance, Schedule and Inventory - Products; the sub-tabs sit right below.',
        'Accounts and cash became Financial accounts and Categories became Chart of accounts, both in the Finance tab.',
        'In Services, the Service catalog is what the company sells; Services is the new record of what it hires from third parties.',
        'On the contracted service, choose the expense category and the default supplier: the direct purchase suggests both on the item.',
      ],
    },
    es: {
      titulo: 'Registros en cinco pestañas y los servicios contratados',
      resumo: 'Registros, Servicios, Finanzas, Agenda e Inventario - Productos, cada una con sus subpestañas. Y un registro nuevo de servicios contratados.',
      passos: [
        'En Registros, las pestañas de arriba son Registros, Servicios, Finanzas, Agenda e Inventario - Productos; las subpestañas van debajo.',
        'Cuentas y cajas pasó a Cuentas financieras y Categorías pasó a Plan de cuentas, las dos en la pestaña Finanzas.',
        'En Servicios, el Catálogo de servicios es lo que la empresa vende; Servicios es el registro nuevo de lo que contrata a terceros.',
        'En el servicio contratado, elige la categoría de gasto y el proveedor predeterminado: la compra directa sugiere los dos en el ítem.',
      ],
    },
  },
  imagens: ['/novidades/2026-10-05-cadastros-abas/servicos.webp', null, null, null],
  treinamento: { curso: 'novidades' },
};

export const novidadeFinanceiroLinha: Novidade = {
  id: '2026-10-05-financeiro-linha',
  publicada_em: '2026-10-05',
  tipo: 'melhoria',
  modulo: 'financeiro',
  permissao: null,
  rota: '/financeiro/contas/a-receber',
  textos: {
    pt: {
      titulo: 'Financeiro numa linha só de abas',
      resumo: 'A receber, A pagar, Cobranças, Recorrências, Notas de crédito, Movimentações, Conciliação, Fluxo de caixa e Índices, lado a lado.',
      passos: [
        'O menu Financeiro agora tem uma linha só de abas, sem a troca entre Contas a receber e a pagar e Financeiro.',
        'Entradas e saídas passou a se chamar Movimentações. Os dados não mudaram.',
      ],
    },
    en: {
      titulo: 'Finance in a single row of tabs',
      resumo: 'Receivable, Payable, Collections, Recurring, Credit notes, Transactions, Reconciliation, Cash flow and Indices, side by side.',
      passos: [
        'The Finance menu now has a single row of tabs, without switching between Receivables and payables and Finance.',
        'Income and expenses is now called Transactions. The data did not change.',
      ],
    },
    es: {
      titulo: 'Finanzas en una sola fila de pestañas',
      resumo: 'Por cobrar, Por pagar, Cobranzas, Recurrencias, Notas de crédito, Movimientos, Conciliación, Flujo de caja e Índices, lado a lado.',
      passos: [
        'El menú Finanzas ahora tiene una sola fila de pestañas, sin cambiar entre Cuentas por cobrar y por pagar y Finanzas.',
        'Ingresos y egresos ahora se llama Movimientos. Los datos no cambiaron.',
      ],
    },
  },
  imagens: ['/novidades/2026-10-05-financeiro-linha/financeiro.webp', null],
  treinamento: { curso: 'novidades' },
};

export const novidadeRelatoriosMenu: Novidade = {
  id: '2026-10-05-relatorios-menu',
  publicada_em: '2026-10-05',
  tipo: 'melhoria',
  modulo: 'relatorios',
  permissao: 'relatorios.ver',
  rota: '/relatorios/relatorios/inicio',
  textos: {
    pt: {
      titulo: 'Relatórios no menu lateral',
      resumo: 'Relatórios saiu do Financeiro e virou item próprio do menu, com uma página de entrada com um cartão por relatório.',
      passos: [
        'Clique em Relatórios no menu lateral: a página de entrada mostra um cartão por relatório, com uma descrição curta.',
        'O cartão abre o relatório, que continua igual. As abas de cima levam de um relatório a outro.',
        'Links e favoritos antigos de Financeiro > Relatórios levam ao endereço novo.',
      ],
    },
    en: {
      titulo: 'Reports in the side menu',
      resumo: 'Reports left Finance and became its own menu item, with an entry page that has a card per report.',
      passos: [
        'Click Reports in the side menu: the entry page shows a card per report, with a short description.',
        'The card opens the report, which stays the same. The top tabs take you from one report to another.',
        'Old links and bookmarks of Finance > Reports take you to the new address.',
      ],
    },
    es: {
      titulo: 'Informes en el menú lateral',
      resumo: 'Informes salió de Finanzas y es un ítem propio del menú, con una página de entrada con una tarjeta por informe.',
      passos: [
        'Haz clic en Informes en el menú lateral: la página de entrada muestra una tarjeta por informe, con una descripción corta.',
        'La tarjeta abre el informe, que sigue igual. Las pestañas de arriba llevan de un informe a otro.',
        'Los enlaces y favoritos antiguos de Finanzas > Informes llevan a la dirección nueva.',
      ],
    },
  },
  imagens: ['/novidades/2026-10-05-relatorios-menu/relatorios.webp', null, null],
  treinamento: { curso: 'novidades' },
};

export const novidadeVersaoNoAr: Novidade = {
  id: '2026-10-05-versao-no-ar',
  publicada_em: '2026-10-05',
  tipo: 'correcao',
  modulo: 'admin',
  permissao: 'admin.ver',
  rota: '/admin/admin/painel',
  textos: {
    pt: {
      titulo: 'Versão no ar cabe no card',
      resumo: 'No Painel admin, a Versão no ar mostra os 7 primeiros caracteres; o código inteiro aparece ao passar o mouse.',
      passos: ['Abra Admin > Painel admin > Painel.', 'Passe o mouse sobre a versão para ver o código inteiro.'],
    },
    en: {
      titulo: 'Live version fits the card',
      resumo: 'In the Admin panel, the live version shows the first 7 characters; the full code shows on hover.',
      passos: ['Open Admin > Admin panel > Dashboard.', 'Hover over the version to see the full code.'],
    },
    es: {
      titulo: 'La versión en línea cabe en la tarjeta',
      resumo: 'En el Panel admin, la versión en línea muestra los primeros 7 caracteres; el código completo aparece al pasar el mouse.',
      passos: ['Abre Admin > Panel admin > Panel.', 'Pasa el mouse sobre la versión para ver el código completo.'],
    },
  },
  imagens: ['/novidades/2026-10-05-versao-no-ar/painel.webp', null],
  treinamento: { curso: 'novidades' },
};
